import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
} from "recharts";
import KPICardsGrid from "../../../commonUI/Cards/KPICardsGrid";
import TooltipIcon from "../../../commonUI/Tooltip/TooltipIcon";
import {
  BarGraphIcon1,
  BarGraphIcon2,
  BarGraphIcon3,
  ChevronDownIcon,
  SMSIcon,
  PushNotificationIcon,
  EmailNotificationIcon,
  TimelineIcon,
} from "../../../commonUI/Icons/SvgIcon";
import {
  NOTIFICATION_KPI_CARDS,
  NOTIFICATION_CATEGORY_DATA,
  NOTIFICATION_CHART_DATA,
} from "../../../../config/notificationConstants";

const CHART_INTERVALS = ["Daily", "Weekly", "Monthly"];

const ChannelBadge = ({ channel }) => {
  const cls =
    channel === "SMS"
      ? "notif-channel-badge notif-channel-badge--sms"
      : channel === "Push"
        ? "notif-channel-badge notif-channel-badge--push"
        : "notif-channel-badge notif-channel-badge--email";

  return (
    <span className={cls}>
      {channel === "SMS" && <SMSIcon width="12" height="12" />}
      {channel === "Push" && <PushNotificationIcon width="12" height="12" />}
      {channel === "Email" && <EmailNotificationIcon width="12" height="12" />}
      {channel}
    </span>
  );
};

const CategoryRow = ({ row, selectedChannel, onViewHistory }) => {
  const [expanded, setExpanded] = useState(false);

  const visibleChildren =
    selectedChannel === "all"
      ? row.children
      : row.children.filter((c) => c.channel === selectedChannel);

  return (
    <>
      <tr
        className={`notif-table__row notif-table__row--parent${expanded ? " notif-table__row--expanded" : ""}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="notif-table__cell notif-table__cell--name">
          <span className="notif-table__expand-icon">
            <ChevronDownIcon
              className={`notif-table__chevron${expanded ? " notif-table__chevron--up" : ""}`}
            />
          </span>
          <span className="notif-table__category-name">{row.name}</span>
        </td>
        <td className="notif-table__cell notif-table__cell--num"></td>
        <td className="notif-table__cell notif-table__cell--num">
          {row?.sent}
        </td>
        <td className="notif-table__cell notif-table__cell--num">
          {row?.failed}
        </td>
        <td className="notif-table__cell notif-table__cell--num">
          {row?.delivered}
        </td>
        <td className="notif-table__cell notif-table__cell--num">
          {row?.rate}
        </td>
        <td className="notif-table__cell notif-table__cell--action">
          <button
            className="notif-table__chart-btn"
            type="button"
            title="View history"
            onClick={() => onViewHistory(row.id)}
          >
            <TimelineIcon />
          </button>
        </td>
      </tr>

      {expanded &&
        visibleChildren.map((child, idx) => (
          <tr key={idx} className="notif-table__row notif-table__row--child">
            <td className="notif-table__cell notif-table__cell--child-name">
              <span className="notif-table__child-indent" />
              {child.name}
            </td>
            <td className="notif-table__cell notif-table__cell--num">
              <ChannelBadge channel={child.channel} />
            </td>
            <td className="notif-table__cell notif-table__cell--num">
              {child.sent}
            </td>
            <td className="notif-table__cell notif-table__cell--num">
              {child.failed}
            </td>
            <td className="notif-table__cell notif-table__cell--num">
              {child.delivered}
            </td>
            <td className="notif-table__cell notif-table__cell--num">
              {child.rate}
            </td>
            <td className="notif-table__cell notif-table__cell--action">
              <button
                className="notif-table__chart-btn"
                type="button"
                title="View history"
                onClick={() => onViewHistory(row.id)}
              >
                <TimelineIcon />
              </button>
            </td>
          </tr>
        ))}
    </>
  );
};

// ── Stacked delivery chart ────────────────────────────────────────────────────
const DeliveryChart = ({ chartType }) => {
  const commonProps = {
    data: NOTIFICATION_CHART_DATA,
    margin: { top: 10, right: 20, left: 0, bottom: 10 },
  };
  const axisProps = {
    xAxisDataKey: "date",
    xStroke: "#6b7280",
    yStroke: "#6b7280",
    fontSize: "12px",
  };

  const bars = [
    { key: "delivered", color: "#10b981", name: "Delivered" },
    { key: "unconfirmed", color: "#3b82f6", name: "Delivery unconfirmed" },
    { key: "failed", color: "#f87171", name: "Failed to send" },
  ];

  if (chartType === "area") {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart {...commonProps}>
          <defs>
            {bars.map((b) => (
              <linearGradient
                key={b.key}
                id={`grad-${b.key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={b.color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={b.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
          <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
          <Tooltip
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
            }}
          />
          <Legend />
          {bars.map((b) => (
            <Area
              key={b.key}
              type="monotone"
              dataKey={b.key}
              stroke={b.color}
              strokeWidth={2}
              fill={`url(#grad-${b.key})`}
              name={b.name}
              stackId="1"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
          <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
          <Tooltip
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
            }}
          />
          <Legend />
          {bars.map((b) => (
            <Line
              key={b.key}
              type="monotone"
              dataKey={b.key}
              stroke={b.color}
              strokeWidth={2.5}
              dot={{ fill: b.color, r: 3 }}
              name={b.name}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Default: stacked bar
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
        <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
        <Tooltip
          contentStyle={{
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
          }}
        />
        <Legend />
        {bars.map((b) => (
          <Bar
            key={b.key}
            dataKey={b.key}
            stackId="a"
            fill={b.color}
            name={b.name}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Main Overview Component ───────────────────────────────────────────────────
const NotificationOverview = ({
  selectedCategory,
  selectedChannel,
  onViewHistory,
}) => {
  const [chartType, setChartType] = useState("bar");
  const [chartInterval, setChartInterval] = useState("Daily");
  const [showIntervalDrop, setShowIntervalDrop] = useState(false);

  const visibleCategories =
    selectedCategory === "all"
      ? NOTIFICATION_CATEGORY_DATA
      : NOTIFICATION_CATEGORY_DATA.filter((c) => c.id === selectedCategory);

  return (
    <>
      {/* KPI Cards */}
      <div className="notif-kpi-section">
        <KPICardsGrid cards={NOTIFICATION_KPI_CARDS} />
      </div>

      {/* Category Table */}
      <div className="notif-table-section">
        <table className="notif-table">
          <thead>
            <tr className="notif-table__head-row">
              <th className="notif-table__head-cell notif-table__head-cell--name">
                Message category
              </th>
              <th className="notif-table__head-cell"></th>
              <th className="notif-table__head-cell">
                Sent <TooltipIcon title="Total notifications sent" inline />
              </th>
              <th className="notif-table__head-cell">
                Failed to send{" "}
                <TooltipIcon
                  title="Notifications that failed to deliver"
                  inline
                />
              </th>
              <th className="notif-table__head-cell">
                Delivered{" "}
                <TooltipIcon
                  title="Notifications confirmed as delivered"
                  inline
                />
              </th>
              <th className="notif-table__head-cell">
                Delivery rate{" "}
                <TooltipIcon
                  title="Percentage of sent notifications delivered"
                  inline
                />
              </th>
              <th className="notif-table__head-cell" />
            </tr>
          </thead>
          <tbody>
            {visibleCategories.map((row) => (
              <CategoryRow
                key={row.id}
                row={row}
                selectedChannel={selectedChannel}
                onViewHistory={onViewHistory}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Delivery Chart */}
      <div className="notif-chart-section">
        <div className="notif-chart-section__header">
          <h3 className="notif-chart-section__title">Notification delivery</h3>
          <div className="notif-chart-section__controls">
            {/* Interval selector */}
            <div
              className="notif-chart-interval"
              style={{ position: "relative" }}
            >
              <button
                className="notif-chart-interval__btn"
                onClick={() => setShowIntervalDrop((v) => !v)}
                type="button"
              >
                {chartInterval}
                <ChevronDownIcon className="notif-dropdown__chevron" />
              </button>
              {showIntervalDrop && (
                <div className="notif-chart-interval__menu">
                  {CHART_INTERVALS.map((iv) => (
                    <button
                      key={iv}
                      className={`notif-chart-interval__item${chartInterval === iv ? " notif-chart-interval__item--active" : ""}`}
                      onClick={() => {
                        setChartInterval(iv);
                        setShowIntervalDrop(false);
                      }}
                      type="button"
                    >
                      {iv}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Chart type toggles */}
            <div className="notif-chart-type-btns">
              <button
                className={`notif-chart-type-btn${chartType === "bar" ? " notif-chart-type-btn--active" : ""}`}
                onClick={() => setChartType("bar")}
                type="button"
                title="Bar chart"
              >
                <BarGraphIcon1 />
              </button>
              <button
                className={`notif-chart-type-btn${chartType === "area" ? " notif-chart-type-btn--active" : ""}`}
                onClick={() => setChartType("area")}
                type="button"
                title="Area chart"
              >
                <BarGraphIcon2 />
              </button>
              <button
                className={`notif-chart-type-btn${chartType === "line" ? " notif-chart-type-btn--active" : ""}`}
                onClick={() => setChartType("line")}
                type="button"
                title="Line chart"
              >
                <BarGraphIcon3 />
              </button>
            </div>
          </div>
        </div>
        <div className="notif-chart-section__body">
          <DeliveryChart chartType={chartType} />
        </div>
      </div>
    </>
  );
};

export default NotificationOverview;
